import { useState, useRef, useCallback } from "react"
import {
  FileText, FolderPlus, Upload, Download, Trash2, MoreHorizontal,
  File, Image, FileSpreadsheet, Folder, ChevronRight, Home,
  HardDrive, Search,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { toast } from "sonner"
import {
  useDocuments,
  useFolders,
  useUploadDocument,
  useDeleteDocument,
  useDocumentDownload,
  useCreateFolder,
  useDeleteFolder,
  useStorageQuota,
} from "@/hooks/use-documents"
import type { DocumentItem, DocumentFolder } from "@/lib/types"

function formatFileSize(bytes: number | null): string {
  if (!bytes) return "-"
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

function getFileIcon(contentType: string | null) {
  if (!contentType) return File
  if (contentType.startsWith("image/")) return Image
  if (contentType.includes("spreadsheet") || contentType.includes("csv") || contentType.includes("excel")) return FileSpreadsheet
  if (contentType.includes("pdf")) return FileText
  return File
}

function getFileColor(contentType: string | null): string {
  if (!contentType) return "var(--text-muted)"
  if (contentType.startsWith("image/")) return "#8B5CF6"
  if (contentType.includes("pdf")) return "#EF4444"
  if (contentType.includes("spreadsheet") || contentType.includes("csv") || contentType.includes("excel")) return "#10B981"
  if (contentType.includes("word") || contentType.includes("document")) return "#3B82F6"
  return "var(--text-muted)"
}

export function DocumentsPage() {
  const [currentFolderId, setCurrentFolderId] = useState<string | undefined>(undefined)
  const [breadcrumbs, setBreadcrumbs] = useState<Array<{ id: string; name: string }>>([])
  const [showNewFolder, setShowNewFolder] = useState(false)
  const [newFolderName, setNewFolderName] = useState("")
  const [searchQuery, setSearchQuery] = useState("")
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data: docsData, isLoading: docsLoading } = useDocuments(
    currentFolderId ? { folder_id: currentFolderId } : { root: true }
  )
  const { data: foldersData, isLoading: foldersLoading } = useFolders(currentFolderId)
  const { data: storageData } = useStorageQuota()

  const uploadDoc = useUploadDocument()
  const deleteDoc = useDeleteDocument()
  const downloadDoc = useDocumentDownload()
  const createFolder = useCreateFolder()
  const deleteFolder = useDeleteFolder()

  const documents = docsData?.data ?? []
  const folders = foldersData?.data ?? []
  const storage = storageData?.data

  const isLoading = docsLoading || foldersLoading

  const filteredDocs = searchQuery
    ? documents.filter((d) => d.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : documents

  const filteredFolders = searchQuery
    ? folders.filter((f) => f.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : folders

  const navigateToFolder = (folder: DocumentFolder) => {
    setBreadcrumbs((prev) => [...prev, { id: folder.id, name: folder.name }])
    setCurrentFolderId(folder.id)
  }

  const navigateToRoot = () => {
    setBreadcrumbs([])
    setCurrentFolderId(undefined)
  }

  const navigateToBreadcrumb = (index: number) => {
    if (index === -1) {
      navigateToRoot()
    } else {
      const target = breadcrumbs[index]
      setBreadcrumbs(breadcrumbs.slice(0, index + 1))
      setCurrentFolderId(target.id)
    }
  }

  const handleUpload = useCallback(
    (files: FileList | File[]) => {
      Array.from(files).forEach((file) => {
        uploadDoc.mutate(
          { file, folder_id: currentFolderId },
          {
            onSuccess: () => toast.success(`Przesłano: ${file.name}`),
            onError: () => toast.error(`Błąd przesyłania: ${file.name}`),
          }
        )
      })
    },
    [currentFolderId, uploadDoc]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragOver(false)
      if (e.dataTransfer.files.length > 0) {
        handleUpload(e.dataTransfer.files)
      }
    },
    [handleUpload]
  )

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = () => setIsDragOver(false)

  const handleDownload = (doc: DocumentItem) => {
    downloadDoc.mutate(doc.id, {
      onSuccess: (res) => {
        window.open(res.data.url, "_blank")
      },
      onError: () => toast.error("Błąd pobierania pliku"),
    })
  }

  const handleDelete = (doc: DocumentItem) => {
    deleteDoc.mutate(doc.id, {
      onSuccess: () => toast.success("Usunięto plik"),
      onError: () => toast.error("Błąd usuwania pliku"),
    })
  }

  const handleDeleteFolder = (folder: DocumentFolder) => {
    deleteFolder.mutate(folder.id, {
      onSuccess: () => toast.success("Usunięto folder"),
      onError: () => toast.error("Błąd usuwania folderu"),
    })
  }

  const handleCreateFolder = () => {
    if (!newFolderName.trim()) return
    createFolder.mutate(
      { name: newFolderName.trim(), parent_id: currentFolderId },
      {
        onSuccess: () => {
          toast.success("Utworzono folder")
          setShowNewFolder(false)
          setNewFolderName("")
        },
        onError: () => toast.error("Błąd tworzenia folderu"),
      }
    )
  }

  return (
    <div
      className="space-y-4"
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText size={20} className="text-primary" />
          <h1 className="page-title">
            Dokumenty
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {storage && (
            <div className="flex items-center gap-2 mr-4">
              <HardDrive size={14} className="text-muted-foreground" />
              <div className="w-24 h-1.5 rounded-full bg-muted">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${Math.min(storage.usage_percentage, 100)}%`,
                    backgroundColor: storage.usage_percentage > 90 ? "#EF4444" : storage.usage_percentage > 70 ? "#F59E0B" : "var(--primary)",
                  }}
                />
              </div>
              <span className="text-xs text-muted-foreground">
                {formatFileSize(storage.used_bytes)} / {formatFileSize(storage.quota_bytes)}
              </span>
            </div>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowNewFolder(true)}
          >
            <FolderPlus size={14} className="mr-1" />
            Nowy folder
          </Button>
          <Button
            size="sm"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload size={14} className="mr-1" />
            Prześlij pliki
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files) handleUpload(e.target.files)
              e.target.value = ""
            }}
          />
        </div>
      </div>

      {/* Breadcrumbs */}
      <div className="flex items-center gap-1 text-sm">
        <button
          onClick={navigateToRoot}
          className={`flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-muted/30 transition-colors ${breadcrumbs.length === 0 ? "text-foreground" : "text-muted-foreground"}`}
        >
          <Home size={14} />
          <span>Dokumenty</span>
        </button>
        {breadcrumbs.map((crumb, i) => (
          <span key={crumb.id} className="flex items-center gap-1">
            <ChevronRight size={12} className="text-muted-foreground" />
            <button
              onClick={() => navigateToBreadcrumb(i)}
              className={`px-1.5 py-0.5 rounded hover:bg-muted/30 transition-colors ${i === breadcrumbs.length - 1 ? "text-foreground" : "text-muted-foreground"}`}
            >
              {crumb.name}
            </button>
          </span>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-xs">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Szukaj plików..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-8 h-8 text-sm"
        />
      </div>

      {/* Drag & Drop overlay */}
      {isDragOver && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/20"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <div
            className="rounded-xl border-2 border-dashed border-primary bg-card p-12 text-center"
          >
            <Upload size={48} className="text-primary mx-auto mb-4" />
            <p className="text-lg font-medium text-foreground">
              Upuść pliki tutaj
            </p>
            <p className="text-sm mt-1 text-muted-foreground">
              Pliki zostaną przesłane do bieżącego folderu
            </p>
          </div>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      )}

      {/* Content */}
      {!isLoading && (
        <div className="surface-card overflow-hidden">
          {/* Folders */}
          {filteredFolders.length > 0 && (
            <div className="border-b">
              <div className="px-4 py-2 text-xs font-medium text-muted-foreground bg-muted/30">
                Foldery
              </div>
              {filteredFolders.map((folder) => (
                <div
                  key={folder.id}
                  className="flex items-center gap-3 px-4 py-2.5 border-b last:border-b-0 cursor-pointer transition-colors hover:bg-muted/30"
                  onClick={() => navigateToFolder(folder)}
                >
                  <Folder size={18} style={{ color: folder.color || "#F59E0B" }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate text-foreground">
                      {folder.name}
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {folder.documents_count} {folder.documents_count === 1 ? "plik" : "plików"}
                  </span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                        <MoreHorizontal size={14} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteFolder(folder)
                        }}
                        className="text-red-600"
                      >
                        <Trash2 size={14} className="mr-2" />
                        Usuń folder
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </div>
          )}

          {/* Files */}
          {filteredDocs.length > 0 && (
            <div>
              <div className="px-4 py-2 text-xs font-medium text-muted-foreground bg-muted/30">
                Pliki
              </div>
              {filteredDocs.map((doc) => {
                const Icon = getFileIcon(doc.content_type)
                const color = getFileColor(doc.content_type)

                return (
                  <div
                    key={doc.id}
                    className="flex items-center gap-3 px-4 py-2.5 border-b last:border-b-0 transition-colors hover:bg-muted/30"
                  >
                    <Icon size={18} style={{ color }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate text-foreground">
                        {doc.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {doc.uploaded_by ? `${doc.uploaded_by.first_name} ${doc.uploaded_by.last_name}` : ""}
                        {doc.uploaded_by && doc.created_at ? " · " : ""}
                        {doc.created_at ? new Date(doc.created_at).toLocaleDateString("pl-PL") : ""}
                      </p>
                    </div>
                    <span className="text-xs shrink-0 text-muted-foreground">
                      {formatFileSize(doc.file_size)}
                    </span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                          <MoreHorizontal size={14} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleDownload(doc)}>
                          <Download size={14} className="mr-2" />
                          Pobierz
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDelete(doc)}
                          className="text-red-600"
                        >
                          <Trash2 size={14} className="mr-2" />
                          Usuń
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )
              })}
            </div>
          )}

          {/* Empty state */}
          {filteredFolders.length === 0 && filteredDocs.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16">
              <FileText size={48} className="text-muted-foreground opacity-30" />
              <p className="mt-4 text-sm font-medium text-foreground">
                {searchQuery ? "Brak wyników" : "Brak dokumentów"}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {searchQuery ? "Spróbuj zmienić kryteria wyszukiwania" : "Prześlij pliki lub utwórz folder"}
              </p>
              {!searchQuery && (
                <div className="mt-4 flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setShowNewFolder(true)}>
                    <FolderPlus size={14} className="mr-1" />
                    Nowy folder
                  </Button>
                  <Button size="sm" onClick={() => fileInputRef.current?.click()}>
                    <Upload size={14} className="mr-1" />
                    Prześlij pliki
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* New Folder Dialog */}
      <Dialog open={showNewFolder} onOpenChange={setShowNewFolder}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nowy folder</DialogTitle>
          </DialogHeader>
          <Input
            placeholder="Nazwa folderu"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreateFolder()}
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewFolder(false)}>
              Anuluj
            </Button>
            <Button onClick={handleCreateFolder} disabled={!newFolderName.trim()}>
              Utwórz
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
